import React, { ReactNode, forwardRef } from 'react';
import { DOMProps } from '@react-types/shared';
import { genericMemo } from '@direct-frontend/stdlib';

import { TestProps } from '../../internal/types/testTypes';
import { useTypography } from '../../hooks/useTypography/useTypography';
import { useHStack } from '../../hooks/useStack';
import { cn } from '../../lib/css/className/className';
import { useInvertedTheme } from '../../lib/theme';
import './Badge.css';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

const cls = cn('Badge');

export type BadgeSize = '8' | '12' | '16' | '20' | '24';
type BadgeDeprecatedColor = 'black' | 'gray' | 'white' | 'blue' | 'green' | 'yellow' | 'red';
export type BadgeColor = 'accent' | 'message' | 'info' | 'success' | 'warning' | 'error' | BadgeDeprecatedColor;

export interface BadgeProps extends TestProps, DOMProps {
    /**
     * Дочерние элементы
     */
    children?: ReactNode;

    /**
     * Цветовая палитра элемента
     */
    color: BadgeColor;

    /**
     * Размер элемента
     */
    size: BadgeSize;

    /**
     * Признак отключения компонента
     * @default false
     */
    isDisabled?: boolean;

    /**
     * Названия классов для элемента
     */
    className?: string;
}

const skinMap: Record<BadgeColor, SkinRole | SkinColor> = {
    yellow: 'accent',
    green: 'success',
    blue: 'info',
    red: 'error',
    gray: 'message-lc',
    white: 'gray-neutral',
    black: 'gray-neutral',
    accent: 'accent',
    message: 'message-lc',
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error',
};

/**
 * Получение количества символов в дочернем элементе для определения внутреннего отступа.
 * @param children - дочерний элемент (текстовый контент/число)
 * @returns length - количество символов, достаточное для смены стиля
 */
function getChildrenLength(children: ReactNode) {
    const childrenIsText = children && (typeof children === 'string' || typeof children === 'number');
    if (!childrenIsText || children.toString().length <= 1) {
        return undefined;
    }

    return children.toString().length === 2 ? 2 : 3;
}

/**
 * Компонент для создания бейджей
 */
export const Badge = genericMemo(
    forwardRef<HTMLElement, BadgeProps>(function Badge(
        { children, color, size, className, isDisabled, ...props },
        ref,
    ) {
        const { textClasses: typographyClasses } = useTypography({ color: 'current' });

        const stackClasses = useHStack({
            justifyContent: 'center',
            alignItems: 'center',
        });

        const canHaveChildren = Number(size) > 12;
        const length = canHaveChildren ? getChildrenLength(children) : undefined;

        const invertedTheme = useInvertedTheme();
        const skinClass = getColorSkin(skinMap[color], { isDisabled });

        return (
            <span
                {...props}
                className={cls({ color, size, empty: !children, length }, [
                    className,
                    typographyClasses,
                    stackClasses,
                    skinClass,
                    color === 'black' ? invertedTheme : '',
                ])}
                ref={ref}
            >
                {canHaveChildren && children}
            </span>
        );
    }),
);
